'use client';

import React from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface FooterLink {
  label: string;
  href: string;
}

interface FooterSection {
  title: string;
  links: FooterLink[];
}

interface SocialLink {
  platform: string;
  icon: string;
  href: string;
}

interface FooterProps {
  sections: FooterSection[];
  socialLinks: SocialLink[];
  contactInfo: {
    address: string;
    phone: string;
    email: string;
  };
}

const Footer: React.FC<FooterProps> = ({ sections, socialLinks, contactInfo }) => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-primary text-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          <div>
            <div className="flex items-center space-x-3 mb-6">
              <div className="relative">
                <div className="absolute inset-0 bg-accent/20 rounded-lg blur-lg transition-all duration-300" />
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 48 48"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="relative drop-shadow-2xl"
                  style={{ filter: 'drop-shadow(0 4px 12px rgba(212, 175, 55, 0.5))' }}
                >
                  <rect width="48" height="48" rx="8" fill="#D4AF37" />
                  <path d="M14 34V14H18V30H28V34H14Z" fill="#1B365D" />
                  <path d="M28 14H34L28 24L34 34H28L22 24L28 14Z" fill="#1B365D" />
                </svg>
              </div>
              <div className="flex flex-col">
                <span className="font-serif font-bold text-xl leading-tight drop-shadow-lg" style={{ textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8), 0 0 10px rgba(212, 175, 55, 0.3)' }}>
                  Lubbert Associates
                </span>
                <span className="text-accent text-sm font-bold tracking-wider drop-shadow-md" style={{ textShadow: '1px 1px 3px rgba(0, 0, 0, 0.8)' }}>
                  LLC
                </span>
              </div>
            </div>
            <p className="text-white text-sm leading-relaxed mb-6 font-medium drop-shadow-md" style={{ textShadow: '1px 1px 3px rgba(0, 0, 0, 0.8)' }}>
              Your trusted advocate for justice, delivering integrity-driven legal
              representation with compassion and excellence.
            </p>
            <div className="flex gap-3">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-accent hover:text-primary transition-all duration-300 shadow-lg"
                  aria-label={social.platform}
                >
                  <Icon name={social.icon} size={20} />
                </a>
              ))}
            </div>
          </div>

          {sections.map((section, index) => (
            <div key={index}>
              <h3 className="font-semibold text-lg mb-4 drop-shadow-lg" style={{ textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)' }}>{section.title}</h3>
              <ul className="space-y-3">
                {section.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <Link
                      href={link.href}
                      className="text-white hover:text-accent transition-colors duration-300 text-sm font-medium drop-shadow-md"
                      style={{ textShadow: '1px 1px 3px rgba(0, 0, 0, 0.8)' }}
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div>
            <h3 className="font-semibold text-lg mb-4 drop-shadow-lg" style={{ textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)' }}>Contact Us</h3>
            <div className="space-y-3">
              <a
                href="tel:+18078084167"
                className="flex items-center gap-3 text-white hover:text-accent transition-colors duration-300 font-medium drop-shadow-md"
                style={{ textShadow: '1px 1px 3px rgba(0, 0, 0, 0.8)' }}
              >
                <Icon name="PhoneIcon" size={20} variant="outline" />
                <span>+1-807-808-4167</span>
              </a>
              <a
                href="tel:+18078084269"
                className="flex items-center gap-3 text-white hover:text-accent transition-colors duration-300 font-medium drop-shadow-md"
                style={{ textShadow: '1px 1px 3px rgba(0, 0, 0, 0.8)' }}
              >
                <Icon name="PhoneIcon" size={20} variant="outline" />
                <span>Fax: +1-807-808-4269</span>
              </a>
              <a
                href="mailto:zlubbert@lubbertassociatesllc.com"
                className="flex items-center gap-3 text-white hover:text-accent transition-colors duration-300 font-medium drop-shadow-md"
                style={{ textShadow: '1px 1px 3px rgba(0, 0, 0, 0.8)' }}
              >
                <Icon name="EnvelopeIcon" size={20} variant="outline" />
                <span>zlubbert@lubbertassociatesllc.com</span>
              </a>
              <a
                href="mailto:admin@lubbertassociatesllc.com"
                className="flex items-center gap-3 text-white hover:text-accent transition-colors duration-300 font-medium drop-shadow-md"
                style={{ textShadow: '1px 1px 3px rgba(0, 0, 0, 0.8)' }}
              >
                <Icon name="EnvelopeIcon" size={20} variant="outline" />
                <span>Contact: admin@lubbertassociatesllc.com</span>
              </a>
              <div className="flex items-start gap-3 text-white font-medium drop-shadow-md" style={{ textShadow: '1px 1px 3px rgba(0, 0, 0, 0.8)' }}>
                <Icon name="MapPinIcon" size={20} variant="outline" className="flex-shrink-0 mt-1" />
                <span>100 Metcalfe St, Unite: 603<br />Ottawa, ON K1P 5M1 CANADA</span>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white text-sm font-medium drop-shadow-md" style={{ textShadow: '1px 1px 3px rgba(0, 0, 0, 0.8)' }}>
              ©2019 Lubbert Associates LLC.
            </p>
            <div className="flex gap-6">
              <Link
                href="/privacy-policy"
                className="text-white hover:text-accent transition-colors duration-300 text-sm font-medium drop-shadow-md"
                style={{ textShadow: '1px 1px 3px rgba(0, 0, 0, 0.8)' }}
              >
                Privacy Policy
              </Link>
              <Link
                href="/terms-of-service"
                className="text-white hover:text-accent transition-colors duration-300 text-sm font-medium drop-shadow-md"
                style={{ textShadow: '1px 1px 3px rgba(0, 0, 0, 0.8)' }}
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;